import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ContentSpacer } from "../common/layout";
import { useGetTemplateQuery } from "../../data/queries/get-template";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { ArrowLeftIcon, RocketIcon, FileTextIcon } from "lucide-react";
import { ProjectOutlineView } from "../../components/shared/project/project-outline-view";
import { Card } from "../../components/ui/card";
import { UserAvatarAndName } from "../../components/shared/org/avatar-for-user";
import { DateTime } from "luxon";
import { useCreateProjectFromTemplateMutation } from "../../data/mutations/create-project-from-template";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import Spinner from "../../components/ui/spinner";

export function TemplatePreviewPage() {
    const { templateId } = useParams<{ templateId: string }>();
    const query = useGetTemplateQuery(templateId);
    const navigate = useNavigate();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    if (query.isLoading) {
        return (
            <ContentSpacer>
                <div className="mt-8">
                    <Skeleton className="mb-4 h-10 w-64" />
                    <Skeleton className="mb-8 h-6 w-96" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </ContentSpacer>
        );
    }

    if (query.isError || !query.data) {
        return (
            <ContentSpacer>
                <div className="mt-8 rounded-md border border-destructive bg-destructive/10 p-4 text-center">
                    <p className="text-sm text-destructive">
                        Failed to load template. It may have been deleted or you don't have access.
                    </p>
                    <Link to="/templates">
                        <Button variant="outline" className="mt-4">
                            <ArrowLeftIcon className="mr-2 h-4 w-4" />
                            Back to templates
                        </Button>
                    </Link>
                </div>
            </ContentSpacer>
        );
    }

    const template = query.data;
    const createdAt = DateTime.fromISO(template.createdAt);

    return (
        <ContentSpacer>
            <div className="mt-8 flex flex-col gap-6">
                <div>
                    <Link
                        to="/templates"
                        className="group mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-all hover:-translate-x-1 hover:text-foreground"
                    >
                        <ArrowLeftIcon className="h-4 w-4 transition-all group-hover:translate-x-0.5" />
                        Back to templates
                    </Link>
                    <div className="flex items-start justify-between gap-4">
                        <div className="flex min-w-0 flex-1 items-start gap-3">
                            <div className="shrink-0 rounded-md bg-primary/10 p-3">
                                <FileTextIcon className="h-6 w-6 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="line-clamp-2">{template.name}</h2>
                                <p className="mt-1 line-clamp-2 text-muted-foreground">{template.description}</p>
                                <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                                    <span>Created by</span>
                                    <UserAvatarAndName
                                        userId={template.createdBy}
                                        avatarClassName="h-5 w-5"
                                        nameClassName="font-medium text-foreground"
                                    />
                                    <span>·</span>
                                    <span>
                                        {createdAt.toLocaleString({
                                            month: "long",
                                            day: "numeric",
                                            year: "numeric",
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <CreateProjectDialog
                            templateId={template.id}
                            open={createDialogOpen}
                            onOpenChange={setCreateDialogOpen}
                            onSuccess={(projectId) => {
                                setCreateDialogOpen(false);
                                navigate(`/project/${projectId}`);
                            }}
                        >
                            <DialogTrigger asChild>
                                <Button size="lg" className="group shrink-0">
                                    <RocketIcon className="mr-2 h-5 w-5 transition-all group-hover:-rotate-12" />
                                    Use this template
                                </Button>
                            </DialogTrigger>
                        </CreateProjectDialog>
                    </div>
                </div>

                {template.objectives && template.objectives.length > 0 && (
                    <div>
                        <h3 className="mb-3">Objectives</h3>
                        <Card className="p-4">
                            <ul className="list-inside list-disc space-y-2">
                                {template.objectives.map((objective: string, i: number) => (
                                    <li key={i} className="text-sm">
                                        {objective}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    </div>
                )}

                {template.outline && template.outline.workstreams && template.outline.workstreams.length > 0 && (
                    <div>
                        <h3 className="mb-3">Workstreams & Deliverables</h3>
                        <ProjectOutlineView plan={template.outline} />
                    </div>
                )}

                {template.tasks && template.tasks.length > 0 && (
                    <div>
                        <h3 className="mb-3">Tasks</h3>
                        <Card className="p-4">
                            <div className="text-sm text-muted-foreground">
                                This template includes {template.tasks.length} predefined{" "}
                                {template.tasks.length === 1 ? "task" : "tasks"} to help you get started.
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </ContentSpacer>
    );
}

interface CreateProjectDialogProps {
    templateId: string;
    children: React.ReactNode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess: (projectId: string) => void;
}

function CreateProjectDialog({ templateId, children, open, onOpenChange, onSuccess }: CreateProjectDialogProps) {
    const [projectName, setProjectName] = useState("");
    const createMutation = useCreateProjectFromTemplateMutation({
        onSuccess: (projectId) => {
            onSuccess(projectId);
        },
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!projectName.trim()) return;

        createMutation.mutate({
            templateId,
            projectName: projectName.trim(),
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {children}
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create project from template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="projectName">Project name</Label>
                        <Input
                            id="projectName"
                            placeholder="Enter a name for your new project"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            disabled={createMutation.isPending}
                            autoFocus
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={createMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!projectName.trim() || createMutation.isPending}>
                            {createMutation.isPending && <Spinner className="mr-2 h-4 w-4" />}
                            Create project
                        </Button>
                    </div>
                    {createMutation.isError && (
                        <div className="text-sm text-destructive">Failed to create project. Please try again.</div>
                    )}
                </form>
            </DialogContent>
        </Dialog>
    );
}
